import { el, btn } from '@/ui/dom';
import { brandLogo } from '@/ui/brandLogo';
import { GarageStage } from '@/ui/garage/GarageStage';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';
import {
  isValidUsername,
  suggestUsername,
  toUsernameBase,
  usernameCandidates,
  USERNAME_MAX,
} from '@/account/racerNames';
import { cityById } from '@/config/cities';
import { cosmeticById } from '@/config/cosmetics';
import { cosmeticCardArt } from '@/ui/cosmeticArt';
import { vehicleLabel } from '@/ui/vehicleCatalog';
import { UNLOCK_SHOT_S, UNLOCK_SHOTS, type UnlockShot } from '@/ui/onboarding/demoRaceContent';
import { SPEAKER_SVG, SPEAKER_MUTED_SVG } from '@/ui/screens/TitleScreen';
import type { SaveData } from '@/core/SaveData';

/**
 * OnboardingScreen.ts — the M101 post-signup welcome, rebuilt for M112.
 *
 * Runs ONCE per brand-new Racer ID, right after account creation — NEVER for
 * guests (Flow gates it on the server-side profiles.onboarded flag + the
 * hasMeaningfulProgress grandfather rule). The M112 owner spec replaced the
 * old rule-cinematic + spotlight faces with a PLAYABLE DEMO RACE:
 *
 *   0 NAME    — claim a globally-UNIQUE username (M101 machinery untouched).
 *               M114: the name card sits centre-stage ALONE over the owner's
 *               locker-room art (public/onboarding/create-bg.webp) — the
 *               spinning-coupe pedestal now backs only the finale.
 *               Claiming hands Flow the DEMO RACE (cb.onDemo): a live solo
 *               race that freezes under teaching popups — the rule, driving,
 *               ball pushing, item collect + fire (DemoRaceDirector).
 *   1 UNLOCK  — the demo's boost run ends → this 3-shot "unlock video":
 *               courses on tour · rides in the garage · the shop, each an
 *               animated shot of real assets with the owner's verbatim lines.
 *   2 READY   — YOU'RE READY, <NAME>: GET RACING! / LEARN TO PLAY / SHARE.
 *
 * SKIP persists after the name step (the demo race carries its own SKIP chip;
 * faces 1 jump to the finale, the finale exits). The mute toggle rides the
 * DOM faces. The portrait rotate-phone overlay is suppressed while THIS
 * screen is up and restored the moment the demo race takes over (racing is
 * landscape-first; the nudge is part of the lesson).
 */

export interface OnboardingCallbacks {
  /** Batch availability (server truth). null = offline/unknown. */
  checkNames(names: string[]): Promise<Record<string, boolean> | null>;
  /** Claim the name. reason: '' | 'taken' | 'invalid' | 'offline'. */
  claimName(name: string): Promise<{ ok: boolean; reason: string }>;
  /** M112: a successful claim drops the player into the demo race. */
  onDemo(): void;
  shareData(): { code: string | null; claimed: number; points: number };
  onShare(): Promise<'shared' | 'copied' | 'dismissed'>;
  onRace(): void;
  onLearn(): void;
  /** M101b: leave the flow from the persistent SKIP on the finale. */
  onExit(): void;
  /** M101b: flip the persisted mute; returns the NEW state (title pattern). */
  onToggleMute(): boolean;
}

const DICE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<rect x="3.2" y="3.2" width="17.6" height="17.6" rx="4" fill="none" stroke="currentColor" stroke-width="1.9"/>' +
  '<circle cx="8.4" cy="8.4" r="1.7"/><circle cx="15.6" cy="8.4" r="1.7"/>' +
  '<circle cx="12" cy="12" r="1.7"/>' +
  '<circle cx="8.4" cy="15.6" r="1.7"/><circle cx="15.6" cy="15.6" r="1.7"/></svg>';
const STAR_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 2.4 14.9 8.6 21.6 9.4 16.6 14 17.9 20.7 12 17.4 6.1 20.7 7.4 14 2.4 9.4 9.1 8.6Z"/></svg>';
const SHARE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="6" cy="12" r="2.6"/><circle cx="17.5" cy="5.6" r="2.6"/><circle cx="17.5" cy="18.4" r="2.6"/>' +
  '<path d="m8.3 10.9 6.9-3.9 1 1.7-6.9 3.9zM8.3 13.1l6.9 3.9 1-1.7-6.9-3.9z"/></svg>';

export class OnboardingScreen {
  private readonly root: HTMLDivElement;
  private readonly head: HTMLDivElement;
  private readonly skipBtn: HTMLButtonElement;
  private readonly body: HTMLDivElement;

  private stage: GarageStage | null = null;
  private stageHost: HTMLDivElement | null = null;
  private readonly unsubViewport: ViewportUnsubscribe;

  private cb: OnboardingCallbacks | null = null;
  private save: SaveData | null = null;
  /** 0 = name claim · 1 = the unlock video · 2 = the finale. The demo race
   *  lives BETWEEN 0 and 1, outside this screen (Flow + DemoRaceDirector). */
  private face = 0;
  private visible = false;
  private reduced = false;
  private claimedName = '';
  private busy = false;
  /** Debounce/staleness token for the live availability check. */
  private checkSeq = 0;
  private addedRotateDismiss = false;
  private muteBtn!: HTMLButtonElement;
  /** M112: the unlock video's shot-advance ticker. */
  private videoTimer: number | null = null;
  /** M101b: the last generated suggestion — focus clears it for clean typing,
   *  an empty blur brings it back (owner rule). */
  private lastSuggestion = '';

  private readonly onKey = (e: KeyboardEvent): void => {
    if (!this.visible || e.key !== 'Escape') return;
    if (this.face === 1) this.goTo(2);
    else if (this.face === 2) this.cb?.onExit();
  };

  constructor(parent: HTMLElement) {
    this.root = el('div', 'screen screen-onboard screen-cine', parent);
    this.root.style.display = 'none';

    this.head = el('div', 'ob-head', this.root);
    // M101b: the mute toggle rides the whole flow (left corner, title twin).
    this.muteBtn = btn('', 'ob-mute', this.head, () => {
      this.paintMute(this.cb ? this.cb.onToggleMute() : false);
    });
    this.muteBtn.setAttribute('aria-label', 'Mute');
    // SKIP persists on every face after the name step — the unlock video
    // jumps to the finale, the finale leaves the flow (M101b owner rule).
    this.skipBtn = btn('SKIP', 'ob-skip', this.head, () => {
      if (this.face >= 2) this.cb?.onExit();
      else this.goTo(2);
    });

    this.body = el('div', 'ob-body', this.root);

    this.unsubViewport = subscribeViewport(() => {
      this.stage?.resize();
      this.layoutStage();
    });
    window.addEventListener('keydown', this.onKey);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  show(save: SaveData, cb: OnboardingCallbacks): void {
    this.save = save;
    this.cb = cb;
    this.face = 0;
    this.busy = false;
    this.claimedName = '';
    this.lastSuggestion = '';
    this.reduced = save.settings.reducedMotion;
    this.display(save);
  }

  /** M112: return from the demo race WITHOUT resetting the flow — the claimed
   *  name, callbacks and preview mode all carry across the race. */
  reopen(save: SaveData, face: 1 | 2): void {
    if (!this.cb) return; // never shown — nothing to come back to
    this.save = save;
    this.face = face;
    this.display(save);
  }

  private display(save: SaveData): void {
    this.paintMute(save.settings.muted);
    this.root.classList.toggle('reduced', this.reduced);
    // Portrait phones: the rotate nudge must not sit over the welcome — the
    // DOM faces are portrait-capable by design; the nudge returns for racing
    // (hide() restores it, so the demo race gets it back automatically).
    if (!document.body.classList.contains('rotate-dismissed')) {
      document.body.classList.add('rotate-dismissed');
      this.addedRotateDismiss = true;
    }
    this.renderFace();
    this.root.style.display = '';
    this.visible = true;
    // The stage was created while the root was display:none (zero-size host
    // → a 2×2 backing buffer, measured) — re-measure now that we're visible.
    this.stage?.resize();
    this.layoutStage();
  }

  hide(): void {
    if (this.addedRotateDismiss) {
      document.body.classList.remove('rotate-dismissed');
      this.addedRotateDismiss = false;
    }
    this.visible = false;
    this.root.style.display = 'none';
    this.clearVideo();
    this.stage?.hide();
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.unsubViewport();
    this.clearVideo();
    this.stage?.dispose();
    this.root.remove();
  }

  private paintMute(muted: boolean): void {
    this.muteBtn.innerHTML = muted ? SPEAKER_MUTED_SVG : SPEAKER_SVG;
    this.muteBtn.classList.toggle('on', muted);
  }

  private clearVideo(): void {
    if (this.videoTimer !== null) {
      window.clearInterval(this.videoTimer);
      this.videoTimer = null;
    }
  }

  // ---- Face plumbing --------------------------------------------------------

  private goTo(face: number): void {
    if (face === this.face) return;
    this.face = face;
    this.renderFace();
  }

  private renderFace(): void {
    this.clearVideo();
    this.body.replaceChildren();
    // SKIP shows on every face after the name step.
    this.skipBtn.style.display = this.face >= 1 ? '' : 'none';

    if (this.face === 0) this.renderName();
    else if (this.face === 1) this.renderUnlockVideo();
    else this.renderReady();

    // M114: the locker-room art backs ONLY the name face — a root-level class
    // so the photo never rides the .ob-wrap face-in rise. It layers over the
    // opaque base colour, so a missing webp degrades to the plain dark base.
    this.root.classList.toggle('ob-bg-locker', this.face === 0);

    // The showroom pedestal backs face 2 only (M114 — face 0 wears the
    // locker-room photo instead of the spinning coupe); the video owns face 1.
    if (this.face === 2) {
      this.stage?.show();
      if (this.visible) this.stage?.resize(); // the host cell may have changed
      this.layoutStage();
    } else {
      this.stage?.hide();
    }
  }

  /** Shared editorial header: gold kicker + the chrome/gold split logotype. */
  private addHead(parent: HTMLElement, kicker: string): void {
    const k = el('div', 'lb-kicker', parent);
    el('span', 'mhead-rule', k);
    el('span', 'lb-kicker-text', k).textContent = kicker;
    el('span', 'mhead-rule', k);
  }

  /** M148: the owner's logo art (shared builder, shine on the swoops). */
  private addLogo(parent: HTMLElement): void {
    brandLogo(parent, 'ob-logo', { small: true });
  }

  // ---- Face 0: CREATE YOUR RACER (the mandatory username claim) ------------

  private renderName(): void {
    const save = this.save;
    const cb = this.cb;
    if (!save || !cb) return;
    const wrap = el('div', 'ob-wrap ob-face-name', this.body);
    // M114: no media cell — the locker-room art (root .ob-bg-locker) fills
    // the face and the name card takes centre stage alone.
    const card = el('div', 'ob-card', wrap);
    this.addLogo(card);
    this.addHead(card, 'CREATE YOUR RACER');

    const field = el('div', 'ob-field', card);
    const input = document.createElement('input');
    input.className = 'ob-input';
    input.maxLength = USERNAME_MAX;
    input.autocomplete = 'off';
    input.autocapitalize = 'off';
    input.spellcheck = false;
    input.placeholder = 'YOUR RACER NAME';
    field.appendChild(input);
    const chip = el('span', 'ob-sugchip', field);
    chip.textContent = 'SUGGESTED';
    const dice = btn('', 'ob-dice', field, () => void this.rollSuggestion(input, chip, status));
    dice.innerHTML = DICE_SVG;
    dice.setAttribute('aria-label', 'Suggest another name');
    dice.title = 'Suggest another name';

    const status = el('div', 'ob-status', card);
    const errRow = el('div', 'ob-taken', card);
    errRow.style.display = 'none';

    const cont = btn('CONTINUE', 'btn btn-primary ob-cta', card, () => void submit());
    el('div', 'ob-micro', card).textContent = 'YOURS ALONE · CHANGE IT ANYTIME IN YOUR PROFILE';

    const setStatus = (kind: '' | 'ok' | 'bad' | 'wait', text: string): void => {
      status.className = `ob-status${kind ? ` ob-status-${kind}` : ''}`;
      status.textContent = text;
    };
    const clearTaken = (): void => {
      errRow.style.display = 'none';
      errRow.replaceChildren();
    };

    // M101b (owner): tapping into the field CLEARS the suggestion so typing
    // starts clean; leaving it empty brings the suggestion back.
    input.addEventListener('focus', () => {
      if (!field.classList.contains('is-suggested')) return;
      this.lastSuggestion = input.value;
      input.value = '';
      field.classList.remove('is-suggested');
      setStatus('', '');
      clearTaken();
    });
    input.addEventListener('blur', () => {
      if (input.value.trim() !== '') return;
      if (this.lastSuggestion) {
        input.value = this.lastSuggestion;
        field.classList.add('is-suggested');
        setStatus('ok', 'AVAILABLE');
      } else {
        void this.rollSuggestion(input, chip, status);
      }
    });

    // Typing makes the name THEIRS: drop the suggestion look, check live.
    input.addEventListener('keydown', (e) => {
      e.stopPropagation(); // menu keys must never steer the attract cam
      if (e.key === 'Enter') void submit();
    });
    input.addEventListener('input', () => {
      field.classList.remove('is-suggested');
      clearTaken();
      const v = input.value.trim();
      const seq = ++this.checkSeq;
      if (v.length === 0) {
        setStatus('', '');
        return;
      }
      if (!isValidUsername(v)) {
        setStatus('bad', '3–12 CHARACTERS · LETTERS · NUMBERS · _ -');
        return;
      }
      setStatus('wait', 'CHECKING…');
      window.setTimeout(() => {
        if (seq !== this.checkSeq || input.value.trim() !== v) return;
        void cb.checkNames([v]).then((res) => {
          if (seq !== this.checkSeq || input.value.trim() !== v) return;
          if (!res) setStatus('', '');
          else if (res[v]) setStatus('ok', 'AVAILABLE');
          else setStatus('bad', 'TAKEN');
        });
      }, 450);
    });

    const submit = async (): Promise<void> => {
      if (this.busy) return;
      const v = input.value.trim();
      if (!isValidUsername(v)) {
        setStatus('bad', '3–12 CHARACTERS · LETTERS · NUMBERS · _ -');
        card.classList.remove('shake');
        void card.offsetWidth;
        card.classList.add('shake');
        return;
      }
      this.busy = true;
      cont.disabled = true;
      cont.textContent = 'CLAIMING…';
      const res = await cb.claimName(v);
      this.busy = false;
      cont.disabled = false;
      cont.textContent = 'CONTINUE';
      if (res.ok) {
        this.claimedName = v;
        // M112: named → straight into the demo race (Flow owns the swap; it
        // brings us back at the unlock video, or the finale on skip).
        cb.onDemo();
        return;
      }
      if (res.reason === 'taken') {
        setStatus('bad', 'TAKEN');
        await this.showTakenSuggestions(v, errRow, input, field, chip, setStatus);
      } else if (res.reason === 'invalid') {
        setStatus('bad', '3–12 CHARACTERS · LETTERS · NUMBERS · _ -');
      } else {
        setStatus('bad', 'CONNECTION HICCUP — TRY AGAIN');
      }
    };

    // Pre-fill: the player's own prior local name seeds the field if it's a
    // valid username; otherwise a generated, availability-verified suggestion.
    // M103: never seed a generated RACER-XXXX handle as if the player typed
    // it (a device that hosted another account can carry one in the save).
    const seed = toUsernameBase(save.nickname);
    if (isValidUsername(seed) && !/^racer-/i.test(seed)) {
      input.value = seed;
      setStatus('wait', 'CHECKING…');
      void cb.checkNames([seed]).then((res) => {
        if (input.value.trim() !== seed) return;
        if (res && !res[seed]) setStatus('bad', 'TAKEN');
        else if (res) setStatus('ok', 'AVAILABLE');
        else setStatus('', '');
      });
    } else {
      void this.rollSuggestion(input, chip, status);
    }
    // Deliberately NOT auto-focused: the first real click into the field is
    // what clears the suggestion (M101b) — pre-focusing would swallow it.
  }

  /** Generate a batch of candidates, verify against the DB, pre-fill the
   *  first available one styled as a SUGGESTION (dimmed + chip). */
  private async rollSuggestion(
    input: HTMLInputElement,
    chip: HTMLElement,
    status: HTMLElement,
  ): Promise<void> {
    const cb = this.cb;
    if (!cb) return;
    const cands = Array.from({ length: 4 }, () => suggestUsername());
    const seq = ++this.checkSeq;
    const avail = await cb.checkNames(cands);
    if (seq !== this.checkSeq) return; // the player started typing meanwhile
    const pick = avail ? (cands.find((c) => avail[c]) ?? cands[0]) : cands[0];
    input.value = pick;
    this.lastSuggestion = pick;
    const field = input.parentElement;
    field?.classList.add('is-suggested');
    chip.textContent = 'SUGGESTED';
    status.className = 'ob-status ob-status-ok';
    status.textContent = avail ? 'AVAILABLE' : '';
  }

  /** The owner's taken flow: NAME ALREADY TAKEN + up to three similar names
   *  that are verified available, as one-tap chips. */
  private async showTakenSuggestions(
    base: string,
    errRow: HTMLElement,
    input: HTMLInputElement,
    field: HTMLElement,
    chip: HTMLElement,
    setStatus: (kind: '' | 'ok' | 'bad' | 'wait', text: string) => void,
  ): Promise<void> {
    const cb = this.cb;
    if (!cb) return;
    errRow.replaceChildren();
    el('span', 'ob-taken-label', errRow).textContent = 'NAME ALREADY TAKEN — TRY:';
    const cands = usernameCandidates(base, Math.random, 6);
    const avail = await cb.checkNames(cands);
    const free = avail ? cands.filter((c) => avail[c]) : cands;
    for (const cand of free.slice(0, 3)) {
      btn(cand, 'ob-taken-chip', errRow, () => {
        input.value = cand;
        this.lastSuggestion = cand;
        field.classList.add('is-suggested');
        chip.textContent = 'SUGGESTED';
        setStatus('ok', 'AVAILABLE');
        errRow.style.display = 'none';
      });
    }
    errRow.style.display = '';
  }

  // ---- Face 1: the UNLOCK VIDEO (M112 — three animated shots) ---------------

  private renderUnlockVideo(): void {
    const wrap = el('div', 'ob-wrap ob-face-video', this.body);
    const stage = el('div', 'ob-video', wrap);
    const shots = UNLOCK_SHOTS.map((shot) => this.buildShot(shot));
    for (const s of shots) stage.appendChild(s);
    const rail = el('div', 'ob-video-rail', wrap);
    const ticks = UNLOCK_SHOTS.map(() => el('span', 'ob-video-tick', rail));

    let idx = -1;
    const showShot = (i: number): void => {
      if (i >= shots.length) {
        // The video's last frame hands off to the finale (its own entrance
        // animation is the "smooth transition" — the M112 owner ask).
        this.goTo(2);
        return;
      }
      idx = i;
      shots.forEach((s, j) => {
        s.classList.toggle('on', j === i);
        s.classList.toggle('gone', j < i);
      });
      ticks.forEach((t, j) => t.classList.toggle('on', j <= i));
    };
    const arm = (): void => {
      this.clearVideo();
      this.videoTimer = window.setInterval(() => showShot(idx + 1), UNLOCK_SHOT_S * 1000);
    };
    showShot(0);
    arm();
    // A tap plays the next shot early (video-scrub feel; SKIP jumps to the finale).
    stage.addEventListener('pointerdown', () => {
      showShot(idx + 1);
      if (this.videoTimer !== null) arm(); // goTo(2) already cleared it otherwise
    });
  }

  /** One shot: kicker + the owner's line + a band of three real asset cards. */
  private buildShot(shot: UnlockShot): HTMLElement {
    const s = document.createElement('div');
    s.className = 'ob-shot';
    const inner = el('div', 'ob-shot-inner', s);
    this.addHead(inner, shot.kicker);
    const h = el('div', 'ob-headline ob-shot-headline', inner);
    h.textContent = shot.headline;
    h.dataset.text = shot.headline;
    el('div', 'ob-sub ob-shot-sub', inner).textContent = shot.sub;
    const band = el('div', 'ob-shot-band', inner);
    for (const m of shot.media) {
      const card = el('div', 'ob-shot-card', band);
      let label = '';
      if (m.kind === 'city') {
        const img = document.createElement('img');
        img.alt = '';
        img.src = `${import.meta.env.BASE_URL}cities/${m.id}.webp`;
        img.addEventListener('error', () => img.remove());
        card.appendChild(img);
        label = cityById(m.id).name.toUpperCase();
      } else if (m.kind === 'ride') {
        const img = document.createElement('img');
        img.alt = '';
        img.src = `${import.meta.env.BASE_URL}cards/veh_${m.id}.webp`;
        img.addEventListener('error', () => img.remove());
        card.appendChild(img);
        label = vehicleLabel(m.id).toUpperCase();
      } else {
        const item = cosmeticById(m.id);
        if (item) {
          const art = el('div', 'ob-shot-style', card);
          cosmeticCardArt(item, art);
          label = item.name.toUpperCase();
        }
      }
      if (label) el('span', 'ob-shot-cardlabel', card).textContent = label;
    }
    return s;
  }

  // ---- Face 2: YOU'RE READY (the launcher) ---------------------------------

  private renderReady(): void {
    const save = this.save;
    const cb = this.cb;
    if (!save || !cb) return;
    const wrap = el('div', 'ob-wrap ob-face-ready', this.body);
    const media = el('div', 'ob-media', wrap);
    this.ensureStage(media, save);

    const card = el('div', 'ob-card', wrap);
    const burst = el('div', 'ob-burst', card);
    burst.innerHTML = STAR_SVG;
    this.addHead(card, 'WELCOME TO THE TOUR');
    const name = (this.claimedName || save.nickname || 'RACER').toUpperCase();
    const h = el('div', 'ob-headline', card);
    h.textContent = `YOU'RE READY, ${name}`;
    h.dataset.text = `YOU'RE READY, ${name}`;

    const buttons = el('div', 'ob-launch', card);
    btn('GET RACING!', 'btn btn-primary ob-cta', buttons, () => cb.onRace());
    btn('LEARN TO PLAY', 'btn btn-secondary', buttons, () => cb.onLearn());

    // Share-to-earn (sharer-only, 20 SP × up to 5 friends). Hidden in Big Kid
    // (accounts are never advertised there) and while the code is unknown.
    const share = cb.shareData();
    if (save.settings.difficulty !== 'bigkid' && share.code) {
      const shareBtn = btn('', 'btn btn-share', buttons, () => {
        void cb.onShare().then((how) => {
          if (how === 'copied') feedback.textContent = 'LINK COPIED — SEND IT ANYWHERE';
          else if (how === 'shared') feedback.textContent = 'SHARED!';
        });
      });
      const ic = el('span', 'ob-share-ic', shareBtn);
      ic.innerHTML = SHARE_SVG;
      el('span', 'ob-share-label', shareBtn).textContent = 'SHARE · EARN UP TO 100 XP & SP';
      el('div', 'ob-share-sub', buttons).textContent =
        share.claimed > 0
          ? `${share.claimed}/5 FRIENDS JOINED · +${share.points} XP & SP EARNED`
          : '20 XP & SP PER FRIEND WHO JOINS · UP TO 5 FRIENDS';
      const feedback = el('div', 'ob-share-feedback', buttons);
      feedback.setAttribute('aria-live', 'polite');
    }
  }

  // ---- The showroom pedestal (face 2 — the finale) -------------------------

  private ensureStage(host: HTMLElement, save: SaveData): void {
    if (!this.stageHost) {
      this.stageHost = el('div', 'ob-stagehost', host as HTMLElement);
    } else {
      host.appendChild(this.stageHost);
    }
    if (!this.stage) {
      this.stage = new GarageStage(this.stageHost);
      this.stage.setVehicles([{ id: save.settings.playerVehicle, unlocked: true }]);
      this.stage.setHero(save.settings.playerVehicle);
    }
    this.stage.show();
    this.layoutStage();
  }

  /** Park the hero on its pedestal. The stage canvas fills only the MEDIA
   *  zone (grid cell), so the fractions are zone-relative — plain centering,
   *  nearer (larger) on roomier layouts. Bigger yFrac = nearer the camera. */
  private layoutStage(): void {
    if (!this.stage || !this.save) return;
    const w = this.root.clientWidth;
    const h = this.root.clientHeight;
    if (w <= 0 || h <= 0) return;
    const portrait = h > w;
    const short = !portrait && h <= 500;
    this.stage.applyTargets({
      [this.save.settings.playerVehicle]: portrait
        ? { xFrac: 0.5, yFrac: 0.56 }
        : { xFrac: 0.5, yFrac: short ? 0.66 : 0.7 },
    });
  }
}
